import React, { useState, useRef, useEffect, useLayoutEffect } from "react"
import { Cog6ToothIcon, ClipboardDocumentIcon, ClipboardDocumentCheckIcon, ArrowUpTrayIcon } from "@heroicons/react/24/outline"
import { LanguageIcon as LocalizationIcon } from "@heroicons/react/24/outline"
import OptionsMenu from "../OptionsMenu"
import Checkbox from "../Checkbox" // Assuming you have a Checkbox component
import Tooltip from "../Tooltip"
import Button from "aldoo-ra/Button"
import LocalizationItemEditor from "../Localization/localizaiton-item-editor"
import LocalizationAIRewriteDialog from "../Localization/localization-ai-rewrite-dialog"
import generateLocalizationKey from "./generate-localization-key"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import Controller from "./controllers/cms-controller"
import { useFieldClipboard } from "./field-clipboard-context"

const RobotIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h3a3 3 0 0 1 3 3v1h.5a1.5 1.5 0 0 1 0 3H19v1a3 3 0 0 1-3 3H8a3 3 0 0 1-3-3v-1h-.5a1.5 1.5 0 0 1 0-3H5v-1a3 3 0 0 1 3-3h3V5.73A2 2 0 0 1 10 4a2 2 0 0 1 2-2zm-2 9a1.5 1.5 0 0 0 0 3 1.5 1.5 0 0 0 0-3zm4 0a1.5 1.5 0 0 0 0 3 1.5 1.5 0 0 0 0-3zm-5 5v1h6v-1H9z"/>
  </svg>
)

export function FieldImportButton({ field, fieldValues, setFieldValues, dataItem, onSettingChange }) {
  const [showImport, setShowImport] = useState(false)
  const [importText, setImportText] = useState("")
  const { copyField, canPaste, pasteField, startDrag, endDrag } = useFieldClipboard()
  const fieldType = field.type?.value || field.type
  const canPasteToThis = canPaste(fieldType)

  const handleImport = () => {
    const lines = importText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean)
    if (!lines.length) return
    const current = fieldValues[field.id]
    const existing = new Set(
      (Array.isArray(current) ? current : []).map((v) =>
        (typeof v === "object" ? v.value ?? v : v)?.toString().toLowerCase().trim()
      )
    )
    const toAdd = lines.filter((l) => !existing.has(l.toLowerCase()))
    if (!toAdd.length) return
    const updated = [...(Array.isArray(current) ? current : []), ...toAdd]
    fieldValues[field.id] = updated
    if (dataItem.content) dataItem.content[field.id] = updated
    setFieldValues({ ...fieldValues })
    if (onSettingChange) onSettingChange(dataItem, field)
    setImportText("")
    setShowImport(false)
  }

  const handleCopy = () => {
    copyField(field, fieldValues[field.id], fieldType)
  }

  const handlePaste = () => {
    if (!canPasteToThis) return
    const clipboardData = pasteField()
    if (!clipboardData) return
    fieldValues[field.id] = clipboardData.value
    if (dataItem.content) dataItem.content[field.id] = clipboardData.value
    setFieldValues({ ...fieldValues })
    if (onSettingChange) onSettingChange(dataItem, field)
  }

  const handleDragStart = (e) => {
    startDrag(field, fieldValues[field.id], fieldType)
    e.dataTransfer.effectAllowed = "copy"
    e.dataTransfer.setData("text/plain", field.label || field.id)
    const dragImage = document.createElement("div")
    dragImage.style.cssText = "position:absolute;top:-1000px;left:-1000px;background:#3b82f6;border-radius:9999px;padding:8px 16px;font-family:system-ui,sans-serif;color:white;font-weight:600;font-size:13px;"
    dragImage.textContent = field.label || field.id
    document.body.appendChild(dragImage)
    e.dataTransfer.setDragImage(dragImage, 0, 0)
    setTimeout(() => document.body.removeChild(dragImage), 0)
  }

  return (
    <div className="relative justify-end items-center flex flex-row mt-5">
      <div className="mr-3 flex items-center gap-2">
        {/* Copy button */}
        <Tooltip content="Copy or Drag'n'Drop onto a field" position="top">
          <button
            draggable
            onDragStart={handleDragStart}
            onDragEnd={endDrag}
            onClick={handleCopy}
            className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_text_secondary hover:text-admin_primary hover:border-admin_primary transition-colors cursor-grab active:cursor-grabbing"
          >
            <ClipboardDocumentIcon className="w-5 h-5" />
          </button>
        </Tooltip>

        {/* Paste button */}
        {canPasteToThis && (
          <Tooltip content={`Paste to ${field.label || field.id}`} position="top">
            <button
              onClick={handlePaste}
              className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_text_secondary hover:text-admin_primary hover:border-admin_primary transition-colors"
            >
              <ClipboardDocumentCheckIcon className="w-5 h-5" />
            </button>
          </Tooltip>
        )}

        {/* Import button */}
        <div className="relative">
          <Tooltip content="Import items (CSV or one per line)" position="top">
            <button
              onClick={() => { setShowImport((v) => !v); setImportText("") }}
              className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_text_secondary hover:text-admin_primary hover:border-admin_primary transition-colors"
            >
              <ArrowUpTrayIcon className="w-5 h-5" />
            </button>
          </Tooltip>
          {showImport && (
            <div className="absolute right-0 bottom-full mb-2 z-[500] bg-admin_paper border border-admin_border rounded-lg shadow-xl p-3 flex flex-col gap-2 w-72">
              <p className="text-xs text-admin_text_secondary">Paste CSV or one item per line. Duplicates are skipped.</p>
              <textarea
                autoFocus
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                rows={5}
                className="w-full px-2 py-1.5 text-sm border border-admin_border rounded focus:outline-none focus:ring-1 focus:ring-admin_primary bg-admin_paper text-admin_text resize-none"
                placeholder={"tag1, tag2\nor one per line"}
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setShowImport(false)}
                  className="px-3 py-1 text-xs rounded border border-admin_border text-admin_text_secondary hover:bg-admin_border/20 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  className="px-3 py-1 text-xs rounded bg-admin_primary text-white hover:bg-admin_primary/90 transition"
                >
                  Import
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function FieldSettingsButton({
  dataItem,
  field,
  fieldValues,
  setFieldValues,
  onSettingChange,
  contentType,
  //this is a reference to the save method for the content
  //this is used to save the content before adding localization
  onContentSave,
}) {
  //invalid data item passed
  if (!dataItem) return null

  // Do not show the settings button for these field types
  if (
    field.type === "Array" ||
    field.type === "Content" ||
    field.type === "Group" ||
    field.type === "Number" ||
    field.type === "Boolean" ||
    field.type === "DatabaseModel"
  )
    return

  //check if this field is localized by looking into the localization array
  const isLocalized = !!(dataItem.localization?.find(
    (item) => item.field === field.id
  ))

  // Generate localization key dynamically to handle updated instance IDs
  const getLocalizationKey = () => generateLocalizationKey({
    type: contentType.type,
    fieldID: field.id,
    instanceID: dataItem._id,
  })

  const [showMenu, setShowMenu] = useState(false)
  const cogButtonRef = useRef(null) // Ref for the cog button
  const [refresh, setRefresh] = useState(false)
  const [showLocalizationEditor, setShowLocalizationEditor] = useState(false)
  const [showAIRewrite, setShowAIRewrite] = useState(false)
  const controller = Controller() // Initialize controller at component level
  const { copyField, canPaste, pasteField, startDrag, endDrag } = useFieldClipboard()
  const toggleMenu = () => setShowMenu((prev) => !prev)

  const fieldType = field.type?.value || field.type
  const canPasteToThis = canPaste(fieldType)

  const changeSetting = async (value) => {
    if (!value) {
      //remove the localization item
      const updatedLocalization = dataItem.localization?.filter(
        (item) => item.field !== field.id
      )
      dataItem.localization = updatedLocalization
      //announce the change
      if (onSettingChange) onSettingChange(dataItem, field)
      setRefresh(!refresh)
    } else {
      const addLocalization = async () => {
        // Get the current field value BEFORE adding localization
        // This ensures we capture the value before any state changes
        const currentValue = fieldValues[field.id] ?? ""

        //add the localization item
        dataItem.localization = dataItem.localization || []
        const localizationKey = getLocalizationKey()
        dataItem.localization.push({
          field: field.id,
          localizationKey,
        })

        // For saved instances (CASE 5), immediately create the localization entry
        // with the current field value as the English translation
        if (dataItem._id) {
          try {
            const { SaveLocalizationItem } = await import("aldoo-ra/Localization/api")

            await SaveLocalizationItem({
              key: localizationKey,
              language: "en", // Primary language
              content: currentValue,
              type: field.type?.toLowerCase() || "text"
            })
          } catch (error) {
            console.error('Failed to create initial localization entry:', error)
          }
        }

        //announce the change
        if (onSettingChange) onSettingChange(dataItem, field)
        setRefresh(!refresh)
      }

      //-----------------------------------
      //if there is no id, the content is not saved
      //-----------------------------------
      if (!dataItem._id) {
        showPromptDialog({
          title: "Save the content first",
          message:
            "Please save the content before adding localization. Save now?",
          onConfirm: async () => {
            // Get the current field value BEFORE saving
            const currentValue = fieldValues[field.id] ?? ""

            const newItem = await onContentSave()
            if (!newItem) return
            // Update the dataItem with the saved instance
            Object.assign(dataItem, newItem)

            // Add the localization metadata first
            dataItem.localization = dataItem.localization || []
            const localizationKey = getLocalizationKey()
            dataItem.localization.push({
              field: field.id,
              localizationKey,
            })

            // Now create and save the localization entry with current field value
            // This mimics what happens when the localization editor first opens
            try {
              const { SaveLocalizationItem } = await import("aldoo-ra/Localization/api")

              await SaveLocalizationItem({
                key: localizationKey,
                language: "en", // Primary language
                content: currentValue,
                type: field.type?.toLowerCase() || "text"
              })

              // After creating the localization entry, save the content again
              // so that the ContentEditor syncs the localized values and displays
              // the actual content instead of placeholder keys
              await onContentSave()
            } catch (error) {
              console.error('Failed to create initial localization entry:', error)
            }

            // Announce the change and force re-render
            if (onSettingChange) onSettingChange(dataItem, field)
            setRefresh(!refresh)
          },
        })
        return
      }

      //we have a valid id, proceed to add localization
      await addLocalization()
    }
  }

  const editLocalization = () => {
    //proceed to edit localization
    setShowLocalizationEditor(true)
  }

  const handleCopy = () => {
    const value = fieldValues[field.id]
    copyField(field, value, fieldType)
  }

  const handlePaste = () => {
    if (!canPasteToThis) return
    const clipboardData = pasteField()
    if (clipboardData) {
      // Update field value
      fieldValues[field.id] = clipboardData.value
      if (dataItem.content) {
        dataItem.content[field.id] = clipboardData.value
      }
      // Trigger re-render
      const updatedFieldValues = { ...fieldValues }
      setFieldValues(updatedFieldValues)
      if (onSettingChange) {
        onSettingChange(dataItem, field)
      }
    }
  }

  const handleDragStart = (e) => {
    const value = fieldValues[field.id]
    startDrag(field, value, fieldType)
    e.dataTransfer.effectAllowed = "copy"
    e.dataTransfer.setData("text/plain", field.label || field.id)

    // Create custom drag image as a chip
    const dragImage = document.createElement('div')
    dragImage.style.cssText = `
      position: absolute;
      top: -1000px;
      left: -1000px;
      background: #3b82f6;
      border-radius: 9999px;
      padding: 8px 16px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      font-family: system-ui, -apple-system, sans-serif;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      z-index: 10000;
      opacity: 1;
      pointer-events: none;
    `
    dragImage.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" style="width: 16px; height: 16px; color: white;">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
      </svg>
      <span style="color: white; font-weight: 600; font-size: 13px; white-space: nowrap;">
        ${field.label || field.id}
      </span>
    `
    document.body.appendChild(dragImage)
    e.dataTransfer.setDragImage(dragImage, 0, 0)

    // Clean up drag image after drag starts
    setTimeout(() => {
      document.body.removeChild(dragImage)
    }, 0)
  }

  const handleDragEnd = () => {
    endDrag()
  }

  // Menu options
  const menuOptions = [
    {
      id: "localization",
      render: () => (
        <Checkbox
          variant="admin"
          label="Localization"
          isOn={isLocalized}
          onChange={(value) => {
            changeSetting(value)
          }}
        />
      ),
    },
    // Add more options as needed...
  ]

  return (
    <div className="relative justify-end items-center flex flex-row mt-5">
      {/* Send the current values to the localization item editor */}
      <LocalizationItemEditor
        parentObject={dataItem.content}
        isOpen={showLocalizationEditor}
        localizationKey={getLocalizationKey()}
        onClose={() => {
          setShowLocalizationEditor(false)
        }}
        defaultValue={fieldValues[field.id]}
        onSave={async (item) => {
          
          // Update the field value with the new English translation
          const newValue = item.translations.en.content
          
          // Update the field values for the UI
          fieldValues[field.id] = newValue
          
          // IMPORTANT: Also update the original content instance
          if (dataItem.content) {
            dataItem.content[field.id] = newValue
          }
          
          // Create a completely new fieldValues object to ensure React detects the change
          const updatedFieldValues = { ...fieldValues }
          setFieldValues(updatedFieldValues)
          
          // Note: The localization item has already been saved by LocalizationItemEditor
          // We don't need to save the content again, just update the UI state
          
          // Notify parent component of the change to ensure proper synchronization
          if (onSettingChange) {
            onSettingChange(dataItem, field)
          }
        }}
      />

      {/* AI Rewrite dialog for text fields */}
      {showAIRewrite && (
        <LocalizationAIRewriteDialog
          originalText={typeof fieldValues[field.id] === 'string' ? fieldValues[field.id] : ''}
          onAccept={(rewritten) => {
            fieldValues[field.id] = rewritten
            if (dataItem.content) dataItem.content[field.id] = rewritten
            setFieldValues({ ...fieldValues })
            if (onSettingChange) onSettingChange(dataItem, field)
          }}
          onClose={() => setShowAIRewrite(false)}
        />
      )}

      {/* Features */}
      <div className="mr-3 flex items-center gap-2">
        {/* Copy button */}
        <Tooltip
          content="Copy or Drag'n'Drop onto a field"
          position="top"
        >
          <button
            draggable
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onClick={handleCopy}
            className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_text_secondary hover:text-admin_primary hover:border-admin_primary transition-colors cursor-grab active:cursor-grabbing"
          >
            <ClipboardDocumentIcon className="w-5 h-5" />
          </button>
        </Tooltip>

        {/* Paste button - only show if compatible type is in clipboard */}
        {canPasteToThis && (
          <Tooltip
            content={`Paste to ${field.label || field.id}`}
            position="top"
          >
            <button
              onClick={handlePaste}
              className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_text_secondary hover:text-admin_primary hover:border-admin_primary transition-colors"
            >
              <ClipboardDocumentCheckIcon className="w-5 h-5" />
            </button>
          </Tooltip>
        )}

        {/* Show an icon if localized */}
        {isLocalized && (
          <Tooltip
            content="Edit Localization"
            position="top"
          >
            <button
              onClick={editLocalization}
              className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_text_secondary hover:text-admin_primary hover:border-admin_primary transition-colors"
            >
              <LocalizationIcon className="w-5 h-5" />
            </button>
          </Tooltip>
        )}

        {/* AI Rewrite button — text fields only, not when localized */}
        {(fieldType === 'Text' || fieldType === 'String') && !isLocalized && (
          <Tooltip content="AI rewrite this field" position="top">
            <button
              onClick={() => setShowAIRewrite(true)}
              className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_primary hover:border-admin_primary transition-colors"
            >
              <RobotIcon className="w-5 h-5" />
            </button>
          </Tooltip>
        )}

        {/* Cog button */}
        <button
          className="w-8 h-8 rounded-full border border-admin_bordered flex items-center justify-center text-admin_text_secondary hover:text-admin_primary hover:border-admin_primary transition-colors"
          onClick={toggleMenu}
          ref={cogButtonRef}
        >
          <Cog6ToothIcon className="w-5 h-5" />
        </button>

        {/* OptionsMenu */}
        {showMenu && (
          <OptionsMenu
            title="Features"
            options={menuOptions}
            onItemSelect={() => setShowMenu(false)}
            menuPosition="top"
            anchorRef={cogButtonRef}
            hideOnItemSelect={true}
            onClose={() => setShowMenu(false)}
          />
        )}
      </div>
    </div>
  )
}
